import React from 'react';
import { Link } from 'react-router-dom';

import { getProgramsByCategory, urlFor } from '../lib/sanity';

// urlFor returns null when the image is missing, so guard before chaining
const imageUrl = (source, width) => {
  const builder = urlFor(source);
  return builder ? builder.width(width).auto('format').url() : null;
};

const formatDate = (value) => {
  if (!value) return '';
  const parsed = new Date(value);
  if (Number.isNaN(parsed.getTime())) return value;
  return parsed.toLocaleDateString('en-GB', {
    day: 'numeric',
    month: 'long',
    year: 'numeric',
  });
};

// Label the button after whatever the client pasted in
const linkLabel = (url) => {
  if (!url) return 'Learn More';
  if (/facebook\.com|fb\.watch/i.test(url)) return 'View on Facebook';
  if (/youtube\.com|youtu\.be/i.test(url)) return 'Watch on YouTube';
  return 'Learn More';
};

/**
 * Shared layout for the Corporate and Government academy pages — a blog-style
 * list of programs already delivered, backed by the `program` type in Sanity.
 */
function ProgramSection({ category, breadcrumb, titlePlain, titleAccent, subtitle }) {
  const [programs, setPrograms] = React.useState([]);
  const [loading, setLoading] = React.useState(true);
  const [error, setError] = React.useState(null);
  const [selected, setSelected] = React.useState(null);

  React.useEffect(() => {
    let cancelled = false;
    setLoading(true);

    getProgramsByCategory(category)
      .then((data) => {
        if (cancelled) return;
        setPrograms(data || []);
        setLoading(false);
      })
      .catch((err) => {
        if (cancelled) return;
        console.error('Error fetching programs:', err);
        setError(err.message);
        setLoading(false);
      });

    return () => {
      cancelled = true;
    };
  }, [category]);

  // Close the detail modal on Escape, and stop the page behind it scrolling
  React.useEffect(() => {
    if (!selected) return;

    const handleKey = (e) => {
      if (e.key === 'Escape') setSelected(null);
    };
    window.addEventListener('keydown', handleKey);
    document.body.style.overflow = 'hidden';

    return () => {
      window.removeEventListener('keydown', handleKey);
      // Clear the inline value so the stylesheet's overflow-x rule applies again
      document.body.style.overflow = '';
    };
  }, [selected]);

  return (
    <div className="min-h-screen bg-gradient-to-br from-yellow-50 via-white to-orange-50">
      {/* Hero Section */}
      <section className="relative pt-32 pb-16 md:pt-40 md:pb-20">
        <div className="absolute top-10 left-10 w-96 h-96 bg-[#FED700]/20 rounded-full mix-blend-multiply filter blur-3xl opacity-30 animate-pulse"></div>
        <div className="absolute bottom-10 right-10 w-96 h-96 bg-[#F4900C]/20 rounded-full mix-blend-multiply filter blur-3xl opacity-30 animate-pulse"></div>

        <div className="container-custom relative z-10">
          <div className="text-center max-w-4xl mx-auto">
            {/* Breadcrumb */}
            <div className="flex items-center justify-center gap-2 text-sm text-gray-600 mb-4">
              <Link to="/academy" className="hover:text-[#F4900C] transition-colors">
                Academy
              </Link>
              <span>→</span>
              <span className="text-gray-900 font-semibold">{breadcrumb}</span>
            </div>

            <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold text-gray-900 mb-6 leading-tight">
              {titlePlain} <span className="text-[#F4900C]">{titleAccent}</span>
            </h1>

            <p className="text-xl text-gray-600">{subtitle}</p>
          </div>
        </div>
      </section>

      {/* Programs Section */}
      <section className="py-16">
        <div className="container-custom">
          {loading && (
            <div className="text-center py-12">
              <div className="inline-block animate-spin rounded-full h-12 w-12 border-b-2 border-[#F4900C]"></div>
              <p className="text-gray-600 mt-4">Loading programs...</p>
            </div>
          )}

          {error && (
            <div className="text-center py-12">
              <p className="text-red-600 text-lg">Error loading programs: {error}</p>
            </div>
          )}

          {!loading && !error && programs.length === 0 && (
            <div className="text-center py-12">
              <p className="text-gray-600 text-xl">No programs to show yet.</p>
              <p className="text-gray-500 mt-2">Check back soon!</p>
            </div>
          )}

          {!loading && !error && programs.length > 0 && (
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
              {programs.map((program) => (
                <button
                  key={program._id}
                  type="button"
                  onClick={() => setSelected(program)}
                  className="group text-left bg-white rounded-xl shadow-lg overflow-hidden hover:shadow-2xl transition-all duration-300 hover:-translate-y-2 flex flex-col"
                >
                  {/* Main picture — square frame, since the client's Facebook
                      posters are square. object-contain means an image of any
                      other shape is letterboxed rather than cropped, so poster
                      text near an edge never gets cut off. */}
                  <div className="relative aspect-square overflow-hidden bg-gray-50 flex-shrink-0">
                    {imageUrl(program.mainImage, 800) ? (
                      <img
                        src={imageUrl(program.mainImage, 800)}
                        alt={program.name}
                        className="w-full h-full object-contain group-hover:scale-105 transition-transform duration-500"
                      />
                    ) : (
                      <div className="w-full h-full flex items-center justify-center text-gray-400 text-sm">
                        No image
                      </div>
                    )}
                    {program.gallery && program.gallery.length > 0 && (
                      <span className="absolute top-3 right-3 bg-black/60 text-white text-xs font-semibold px-2.5 py-1 rounded-full">
                        📷 {program.gallery.length}
                      </span>
                    )}
                  </div>

                  <div className="p-6 flex flex-col flex-grow">
                    {program.date && (
                      <p className="text-sm text-gray-500 mb-2">{formatDate(program.date)}</p>
                    )}
                    <h3 className="text-xl font-bold text-gray-900 group-hover:text-[#F4900C] transition-colors">
                      {program.name}
                    </h3>
                    {/* Description intentionally omitted — the poster carries
                        the story, and the full text is one click away. */}
                    <span className="mt-auto pt-4 inline-flex items-center text-[#F4900C] font-semibold group-hover:translate-x-2 transition-transform">
                      Read More
                      <svg className="w-5 h-5 ml-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                      </svg>
                    </span>
                  </div>
                </button>
              ))}
            </div>
          )}
        </div>
      </section>

      {/* Program Detail Modal */}
      {selected && (
        <div
          className="fixed inset-0 bg-black/80 z-50 flex items-start justify-center p-4 sm:p-8 overflow-y-auto"
          onClick={() => setSelected(null)}
        >
          {/* `items-start` + `my-auto` centres the panel when it fits and
              top-anchors it when it doesn't. `items-center` would clip the
              top of a tall panel out of reach of the scrollbar. */}
          <div
            className="relative bg-white rounded-2xl shadow-2xl w-full max-w-3xl my-auto"
            onClick={(e) => e.stopPropagation()}
          >
            <button
              onClick={() => setSelected(null)}
              aria-label="Close"
              className="absolute top-3 right-3 z-10 w-9 h-9 rounded-full bg-black/60 text-white text-2xl leading-none hover:bg-black/80 transition-colors"
            >
              ×
            </button>

            {/* Main picture */}
            {imageUrl(selected.mainImage, 1400) && (
              <img
                src={imageUrl(selected.mainImage, 1400)}
                alt={selected.name}
                className="w-full max-h-[60vh] object-contain bg-gray-50 rounded-t-2xl"
              />
            )}

            <div className="p-6 sm:p-8">
              {selected.date && (
                <p className="text-sm text-gray-500 mb-2">{formatDate(selected.date)}</p>
              )}
              <h2 className="text-2xl sm:text-3xl font-bold text-gray-900 mb-4">
                {selected.name}
              </h2>

              <p className="text-gray-700 leading-relaxed whitespace-pre-line">
                {selected.description}
              </p>

              {/* Gallery */}
              {selected.gallery && selected.gallery.length > 0 && (
                <div className="mt-8">
                  <h3 className="font-bold text-gray-900 mb-3">Gallery</h3>
                  <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
                    {selected.gallery.map((photo, index) => {
                      const src = imageUrl(photo, 600);
                      if (!src) return null;
                      return (
                        <img
                          key={photo._key || index}
                          src={src}
                          alt={`${selected.name} — ${index + 1}`}
                          loading="lazy"
                          className="w-full aspect-square object-contain bg-gray-50 rounded-lg"
                        />
                      );
                    })}
                  </div>
                </div>
              )}

              {/* Link */}
              {selected.link && (
                <a
                  href={selected.link}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="mt-8 inline-flex items-center gap-2 bg-gradient-to-r from-[#FED700] to-[#F4900C] hover:from-[#FED700]/90 hover:to-[#F4900C]/90 text-gray-900 font-bold py-3 px-6 rounded-lg shadow-md hover:shadow-lg transition-all duration-300 transform hover:-translate-y-0.5"
                >
                  {linkLabel(selected.link)}
                  <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M14 5l7 7m0 0l-7 7m7-7H3" />
                  </svg>
                </a>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default ProgramSection;
